import { MenuItem } from "prosemirror-menu";
import type { EditorState } from "prosemirror-state";

import { type AlignValue, getTextblockAttrs } from "../adapter/schema";
import type { PathIconSpec } from "../icons";

/** Returns the alignment shared by all top-level blocks in the selection, or "mixed" if blocks have different alignments. */
function computeActiveAlign(state: EditorState): AlignValue | "mixed" {
  const { selection, doc } = state;
  let result: AlignValue | "mixed" | null = null;
  doc.nodesBetween(selection.from, selection.to, (node) => {
    if (result !== "mixed") {
      const { align } = getTextblockAttrs(node);
      if (result === null) result = align;
      else if (result !== align) result = "mixed";
    }
    return false; // Never recurse into child nodes
  });
  return result ?? "left";
}

/**
 * Creates toolbar MenuItems for all three alignment buttons (left, center, right).
 *
 * All three items share a single WeakMap cache keyed by EditorState, so
 * the nodesBetween traversal runs only once per state update regardless of
 * how many buttons check their active state.
 */
export function alignmentItems(
  configs: Array<{ value: AlignValue; icon: PathIconSpec; title: string }>,
): MenuItem[] {
  // ProseMirror calls active() on every MenuItem independently on each state update.
  // Without caching, that means three separate nodesBetween traversals per update.
  // We avoid this by keying on the EditorState object itself: state objects are
  // immutable, so the same object always yields the same result. WeakMap keys are
  // held weakly, so entries for old states are GC'd automatically.
  const cache = new WeakMap<EditorState, AlignValue | "mixed">();

  function getActiveAlign(state: EditorState): AlignValue | "mixed" {
    if (!cache.has(state)) cache.set(state, computeActiveAlign(state));
    return cache.get(state)!;
  }

  return configs.map(({ value, icon, title }) => {
    return new MenuItem({
      title,
      icon,
      active(state) {
        return getActiveAlign(state) === value;
      },
      run(state, dispatch) {
        const isActive = getActiveAlign(state) === value;
        const nextAlign = isActive ? "left" : value;
        const { from, to } = state.selection;
        const tr = state.tr;
        state.doc.nodesBetween(from, to, (_node, pos) => {
          tr.setNodeAttribute(pos, "align", nextAlign);
          return false; // Never recurse into child nodes
        });
        dispatch?.(tr.scrollIntoView());
        return true;
      },
    });
  });
}
